import {
  useReactTable,
  ColumnDef,
  getCoreRowModel,
  flexRender,
  PaginationState,
} from '@tanstack/react-table';
import { Dispatch, ReactNode, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SourceUrlItemDto, SourceUrlStatsDto } from '@/Domain/api/Analytics/dto/SourceUrl.dto';
import { CardContent } from '@/Domain/components/Shared/Card/Card';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationButton,
  PaginationNext,
  PaginationPrevious,
} from '@/Domain/components/Shared/Pagination/pagination';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/Domain/components/Shared/Table/Table';
import { PaginationHelper } from '@/Domain/helper/Pagination.helper';
import { cn } from '@/Domain/utils/cn';

type Props = {
  isError: boolean;
  isLoading: boolean;
  isPlaceholderData: boolean;
  data: SourceUrlStatsDto | undefined;
  pagination: PaginationState;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
};

export const SourceUrlsTable = ({
  isError,
  isLoading,
  isPlaceholderData,
  data,
  pagination,
  setPagination,
}: Props) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'ai_insights.source_urls' });

  const totalPages = Math.ceil((data?.count ?? 0) / pagination.pageSize);
  const currentPage = pagination.pageIndex + 1;
  const showPagination = totalPages > 1 && !isError && !isLoading;

  const pages = useMemo(
    () => PaginationHelper.getRange(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const columns: ColumnDef<SourceUrlItemDto>[] = [
    {
      accessorKey: 'url',
      header: () => t('url'),
      enableSorting: false,
      minSize: 350,
      cell: ({ row }) => (
        <a
          href={row.original.full_url}
          target="_blank"
          rel="noopener noreferrer"
          className="truncate inline-block max-w-lg"
          title={row.original.full_url}
        >
          {row.original.url}
        </a>
      ),
    },
    {
      accessorKey: 'count',
      header: () => t('conversations'),
      cell: (info) => info.getValue<number>(),
      enableSorting: false,
      meta: { align: 'right' },
    },
  ];

  const table = useReactTable({
    data: data?.items || [],
    rowCount: data?.count || 0,
    columns,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    state: {
      pagination,
    },
  });

  const renderBody = (): ReactNode => {
    if (isError) {
      return (
        <TableRow className="hover:!bg-transparent mx-auto border-b-0">
          <TableCell colSpan={2} className="text-sm text-foreground-subtle text-center py-8">
            {t('error', { defaultValue: 'Failed to load data.' })}
          </TableCell>
        </TableRow>
      );
    }

    if (isLoading) {
      return (
        <TableRow className="hover:!bg-transparent mx-auto border-b-0">
          <TableCell colSpan={2} className="py-8">
            <div className="flex justify-center">
              <Spinner className="size-6" />
            </div>
          </TableCell>
        </TableRow>
      );
    }

    if (!data?.items.length) {
      return (
        <TableRow className="hover:!bg-transparent mx-auto border-b-0">
          <TableCell colSpan={2} className="text-sm text-foreground-subtle text-center py-8">
            {t('empty', { defaultValue: 'No source URLs found for this period.' })}
          </TableCell>
        </TableRow>
      );
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.id}>
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            style={{ width: cell.column.getSize() || 'auto' }}
            className={cn(
              cell.column.columnDef.meta?.align === 'right'
                ? 'text-right font-medium'
                : 'text-left',
            )}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  return (
    <CardContent className="pt-0">
      <div className="relative">
        <Table aria-busy={isPlaceholderData}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    <div
                      className={cn(
                        header.column.columnDef.meta?.align === 'right' && 'flex justify-end',
                      )}
                    >
                      <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody
            data-testid="source-urls-table-body"
            className={cn('transition-opacity', isPlaceholderData && 'opacity-40')}
          >
            {renderBody()}
          </TableBody>
        </Table>

        {isPlaceholderData && (
          <div
            data-testid="source-urls-loading-overlay"
            className="absolute inset-0 flex items-center justify-center"
          >
            <Spinner className="size-6" />
          </div>
        )}
      </div>

      {showPagination && (
        <div className="flex items-center justify-center pt-4 gap-3">
          <Pagination>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => table.previousPage()}
                disabled={isPlaceholderData || !table.getCanPreviousPage()}
              >
                {t('previous')}
              </PaginationPrevious>
            </PaginationItem>

            <PaginationContent>
              {pages.map((page, idx) =>
                page === '...' ? (
                  <PaginationEllipsis key={`ellipsis-${idx}`}>…</PaginationEllipsis>
                ) : (
                  <PaginationItem key={page}>
                    <PaginationButton
                      size="icon"
                      isActive={page === currentPage}
                      disabled={isPlaceholderData}
                      onClick={() => setPagination((old) => ({ ...old, pageIndex: page - 1 }))}
                    >
                      {page}
                    </PaginationButton>
                  </PaginationItem>
                ),
              )}
            </PaginationContent>

            <PaginationItem>
              <PaginationNext
                onClick={() => table.nextPage()}
                disabled={isPlaceholderData || !table.getCanNextPage()}
              >
                {t('next')}
              </PaginationNext>
            </PaginationItem>
          </Pagination>
        </div>
      )}
    </CardContent>
  );
};
